<style lang="scss" scoped>
.activity-card {
    border: none;
    color: inherit;
    cursor: pointer;
    display: block;
    margin: 0px;
    text-align: inherit;
    text-decoration: none;
    height: 100%;
    width: 100%;
    position: relative;

    .media-container {
        position: relative;

        .ar {
            position: relative;
            background-position: 50% 50%;
            background-repeat: no-repeat;

            .images {
                position: absolute;
                top: 0px;
                bottom: 0px;
                left: 0px;
                right: 0px;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                border-radius: 4px;

                img {
                    border-radius: 4px;
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    font-family: "object-fit: cover;";
                    transform: scale(1);
                    transition: transform 3s;

                    &.zoomed {
                        transform: scale(1.1);
                    }
                }

                .primary-image {
                    z-index: 9;
                }

                .crossfade-to-images {
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: 10;
                }

                .crossfade-from-images {
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: 11;
                }

                &.opacity-hover {
                    &.video-ready {
                        .primary-image {
                            opacity: 1;
                        }

                        &:hover {
                            .primary-image {
                                opacity: 0;
                            }
                        }
                    }
                }
            }
        }
    }

    .video-wrapper {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;

        > div {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);

            ::v-deep iframe {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
            }
        }
    }

    .transition-opacity {
        transition: opacity 0.4s !important;
    }

    .title {
        white-space: nowrap;
        overflow-x: hidden;
        text-overflow: ellipsis;
        width: 100%;
    }

    .tag-row {
        flex-flow: row;
        width: fit-content;
    }
    .subtitle {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        font-size: 12px;
    }
}
</style>

<template>
    <a v-if="activity" class="activity-card" :href="route('nl.activity', (activity.slug != null ? {'slug': activity.slug} : {'slug': activity.post_name}))">
        <div class="media-container mb-12" ref="card">
            <div class="ar" :style="{ 'padding-top' : `${ (arHeight / arWidth) * 100 }%` }">
                <div v-if="activity.acf && activity.acf.content && activity.acf.content.images" ref="images" class="images" :class="{'opacity-hover': (activity.acf.content.video_id != null && activity.acf.content.video_id != '')}" @mouseenter="cardHover()" @mouseleave="cardLeave()">
                    <template v-if="activity.acf.content.video_id != null && activity.acf.content.video_id != ''">
                        <img class="primary-image transition-opacity" ref="primary" :src="activity.acf.content.images[0].sizes.medium_large" :alt="activity.post_name">
                        <div class="video-wrapper" v-if="cardLoaded">
                            <vimeo-player :video-id="activity.acf.content.video_id" ref="videoplayer" @ready="videoPlayerReady" :player-width="videoClientWidth" :player-height="videoClientHeight" :autoplay="false" :loop="true" :options="vimeoOptions"></vimeo-player>
                        </div>
                    </template>
                    <template v-else>
                        <img class="primary-image" ref="primary" :src="activity.acf.content.images[0].sizes.medium_large" :alt="activity.post_name">
                        <img v-if="fade_from_image" ref="fade_from" class="crossfade-from-images" :src="fade_from_image" :alt="activity.post_name">
                        <img v-if="fade_to_image" ref="fade_to" class="crossfade-to-images" :src="fade_to_image" :alt="activity.post_name">
                    </template>
                </div>
            </div>
        </div>
        <p class="title small" v-html="activity.post_title ? activity.post_title : activity.title.rendered"></p>
        <p v-if="activity.acf && activity.acf.content && activity.acf.content.highlighted_title && !$root.isMobile" class="text-dark font-weight-light small mt-4 subtitle">
            {{ activity.acf.content.highlighted_title }}
        </p>
        <div v-if="categories && categories.length > 0" class="tag-row mt-12" ref="categories" :key="categories.length">
            <div ref="tags" class="tag-col" v-for="(category, index) in categories" :key="index">
                <div class="tag tag-accent-light tag-xs" v-html="category.name" />
            </div>
        </div>
    </a>
</template>

<script>
export default {
    name: "elements.activity-card",

    props: {
        activity: {
            type: Object,
            required: false,
            default: () => {
                return {
                    categories: []
                }
            }
        },

        canHover: {
            type: Boolean,
            required: false,
            default: true
        },

        ratio: {
            type: [String, Boolean],
            default: '181:240',
            required: false
        }
    },

    data: (vm) => ({
        categories: vm.activity != null ? vm.activity.categories : [],
        images: vm.activity != null && vm.activity.acf && vm.activity.acf.content ? vm.activity.acf.content.images : [],
        fade_to_image: null,
        fade_from_image: null,
        fade_to_counter: 1,
        fade_from_counter: 0,
        crossfade: null,
        fading: false,
        oldWidth: document.documentElement.clientWidth,
        loaded: false,
        videoHoverInterval: null,
        videoReady: false,
        videoIsPlaying: false,
        videoInPromise: false,
        videoInitialPlaybackDelayed: false
    }),

    mounted() {
        var self = this;
        self.loaded = true;

        if (this.activity != null) {
            window.addEventListener('resize', _.debounce(() => { // only on width change because URL bar resizing on mobile      
                var newWidth = document.documentElement.clientWidth;
                if (newWidth != self.oldWidth) {
                    self.calculateTags()
                }
                self.oldWidth = newWidth;
            }), 300, { leading: true, trailing: true });

            window.addEventListener('orientationchange', () => {
                this.calculateTags()
            })
            this.calculateTags()
        }
    },

    methods: {
        cardHover() {
            var self = this;
            if (this.activity.acf.content.video_id != null && this.activity.acf.content.video_id != "") {
                if (self.videoReady) {
                    self.handleVideoPlayback();
                } else {
                    this.videoHoverInterval = setInterval(function () {
                        if (self.videoReady) {
                            clearInterval(self.videoHoverInterval);
                            self.videoHoverInterval = null;
                            self.handleVideoPlayback();
                        }
                    }, 100);
                }
            } else {
                this.handleImageFade();
            }
        },

        cardLeave() {
            if (this.activity.acf.content.video_id != null && this.activity.acf.content.video_id != "") {
                this.handleVideoReset();
            } else {
                this.handleResetImageFade();
            }
        },

        handleVideoPlayback() {
            var self = this;
            if (!this.videoIsPlaying) {
                this.videoInPromise = true;
                this.videoIsPlaying = true;
                this.$refs.videoplayer.play().then(function () {
                    if (!self.videoInitialPlaybackDelayed) {
                        setTimeout(function () {
                            self.videoInitialPlaybackDelayed = true;
                            self.$refs.images.classList.add("video-ready");
                        }, 300);
                    }

                    self.videoInPromise = false;
                    if (!self.videoIsPlaying) {
                        self.$refs.videoplayer.pause();
                    }
                });
            }
        },

        handleVideoReset() {
            if (this.videoIsPlaying) {
                this.videoIsPlaying = false;

                if (!this.videoInPromise) {
                    this.$refs.videoplayer.pause();
                }
            }
        },

        handleImageFade() {
            if (!!('ontouchstart' in window)) return
            if (!this.canHover) return
            let self = this;
            if (this.images == false) return
            let image_count = this.images.length
            if (image_count <= 1) return
            let hide_initial_image_timer = 0
            let first_fade_multiplier = 10
            if (this.fading) hide_initial_image_timer = 0
            if (this.fading) first_fade_multiplier = 6

            this.fading = true
            self.fade_from_image = self.images[self.fade_from_counter].sizes.medium_large
            self.fade_to_image = self.images[self.fade_to_counter].sizes.medium_large

            self.$nextTick().then(() => {
                self.crossfade = setTimeout(() => {
                    // Hide the image before transition
                    self.$refs.fade_to.style.opacity = 0;
                    self.$refs.primary.style.opacity = 0;

                    // Start fading to the next image
                    self.fade(self.$refs.fade_from, first_fade_multiplier);
                    self.unfade(self.$refs.fade_to, first_fade_multiplier);
                    self.unzoom(self.$refs.fade_from);
                    self.zoom(self.$refs.fade_to);

                    // check if the next image is available and otherwise take the first
                    if (self.fade_to_counter === image_count - 1) self.fade_from_counter = 0
                    else self.fade_from_counter = self.fade_to_counter + 1

                    // Set the next image
                    self.crossfade = setTimeout(() => {
                        self.fade_from_image = self.images[self.fade_from_counter].sizes.medium_large
                        self.fade(self.$refs.fade_to, 6);
                        self.unfade(self.$refs.fade_from, 6);
                        self.unzoom(self.$refs.fade_to);
                        self.zoom(self.$refs.fade_from);


                        self.crossfade = setTimeout(() => {
                            if (self.fade_from_counter === image_count - 1) self.fade_to_counter = 0
                            else self.fade_to_counter = self.fade_from_counter + 1
                            self.handleImageFade()
                        }, 3000);
                    }, 3000)
                }, hide_initial_image_timer)
            })
        },

        handleResetImageFade() {
            if (!!('ontouchstart' in window)) return
            if (!this.canHover) return
            if (this.images == false || this.images.length <= 0) return
            this.fade_to_image = null;
            this.fade_from_image = null;
            this.fade_to_counter = 1;
            this.fade_from_counter = 0;
            this.$refs.primary.style.opacity = 1;
            this.fading = false;
            clearTimeout(this.crossfade);
        },

        fade(element, multiplier = 1) {
            if (!element) return
            let op = 1;  // initial opacity
            let timer = setInterval(function () {
                if (op <= 0.005) {
                    clearInterval(timer);
                }
                element.style.opacity = op;
                element.style.filter = 'alpha(opacity=' + op * 100 + ")";
                op -= (multiplier * 0.005);
            }, 10);
        },

        unfade(element, multiplier = 1) {
            if (!element) return
            let op = 0.005;  // initial opacity
            let timer = setInterval(function () {
                if (op >= 1) {
                    clearInterval(timer);
                }
                element.style.opacity = op;
                element.style.filter = 'alpha(opacity=' + op * 100 + ")";
                op += (multiplier * 0.005);
            }, 10);
        },

        zoom(element) {
            if (!element) return
            element.classList.add("zoomed");
        },

        unzoom(element) {
            if (!element) return
            element.classList.remove("zoomed");
        },

        calculateTags() {
            this.categories = [...this.typeCategories]
            this.$nextTick().then(() => {
                this.$nextTick().then(() => {
                    let totalWidth = 0
                    let categoriesCount = 0
                    this.$refs.tags.forEach(c => {
                        totalWidth += c.clientWidth
                        if (totalWidth < this.$refs.card.clientWidth) {
                            categoriesCount++
                            return
                        }
                        this.categories = this.categories.splice(0, categoriesCount)
                    });
                })
            })
        },

        videoPlayerReady() {
            this.videoReady = true;
        },
    },

    computed: {
        arWidth() {
            if (!this.ratio) return 0
            return this.ratio.split(':')[0]
        },

        arHeight() {
            if (!this.ratio) return 0
            return this.ratio.split(':')[1]
        },

        typeCategories() {
            return this.activity.categories.filter(c => c.parent === 6)
        },

        cardLoaded() {
            return this.loaded;
        },

        videoClientHeight() {
            return this.$refs.primary.clientHeight;
        },

        videoClientWidth() {
            return this.$refs.primary.clientHeight / 9 * 16;
        },

        vimeoOptions() {
            return {
                'controls': false,
                'muted': true,
                'autopause': false
            }
        }
    }
}
</script>